import { useEffect, useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import type { ApiFailure, Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrandRefresh } from '@/brand/context'
import { may, useActiveTenant, useTenants } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { CopyButton } from '@/ui/CopyButton'
import { TextField } from '@/ui/Field'
import { Modal } from '@/ui/Modal'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type DomainResponse = Schema<'DomainResponse'>
type DomainRecord = Schema<'DomainRecordOut'>

/**
 * How often the panel re-reads the domain while the edge is provisioning.
 *
 * The `domains.provision` job creates a CloudFront distribution tenant and
 * polls until it is deployed — minutes, not seconds — so fifteen seconds is
 * frequent enough that the screen turns green within a breath of the row
 * doing so, and rare enough that a tab left open all afternoon is not a
 * request every second. Only while `provisioning` (and `verifying`, the
 * moment between the DNS check and the job being queued): a settled row
 * changes only when somebody on this screen changes it.
 */
export const DOMAIN_POLL_MS = 15_000

/** The statuses under which the row is still moving on its own. */
const IN_PROGRESS: ReadonlySet<string> = new Set(['verifying', 'provisioning'])

/** The one code that means "not here", as opposed to "not now". */
const FEATURE_UNAVAILABLE = 'FEATURE_UNAVAILABLE'

/**
 * Settings → Domain: link the organisation's own domain to its workspace.
 *
 * Six states, and every one of them is drawn (spec: *Custom domains*):
 *
 *  - **none** — an input and *Link domain*.
 *  - **pending** — the two DNS records to publish, each with a copy button;
 *    *Verify* and *Remove*. The last verification miss, if any, above them.
 *  - **verifying / provisioning** — a spinner and *Issuing the certificate —
 *    usually a few minutes*, re-reading the row every `DOMAIN_POLL_MS`.
 *  - **verified** — green, the URL, *Remove*.
 *  - **failed** — the reason from the row, *Try again*, the records again
 *    (they are what to fix), *Remove*.
 *  - **unavailable** — `PUT` answered `FEATURE_UNAVAILABLE`: no routing
 *    endpoint is configured in this environment, so there is nothing to
 *    CNAME to. Said plainly rather than as an error to retry.
 *
 * Plus the panel's own three: the read loading, the read failing, and the
 * member without `tenant:update`, who sees the domain and its status and no
 * control. The API refuses their writes regardless; this keeps the screen
 * from offering a 403.
 *
 * `GET /tenants/{id}/domain` is the panel's one read and answers every state
 * in one shape — the tenant, the records, the last error — so a tenant
 * returning a day later sees the same two lines they were shown. The writes
 * all answer the same shape, and the query is re-read after each rather than
 * patched from the response: the row is the truth and the panel is a view of
 * it. After a change the tenant list and `/branding` are refreshed too, since
 * `workspace_url` moves to the custom domain once verified and the lockup
 * links to it.
 */
export function DomainPanel(): JSX.Element {
  const { tenant, membership } = useActiveTenant()
  const editable = may(membership, 'tenant:update')

  const domain = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/tenants/{tenant_id}/domain', {
          params: { path: { tenant_id: tenant.id } },
          signal,
        }),
      ),
    [tenant.id],
  )

  const status = domain.state.data?.tenant.custom_domain_status ?? null
  const inProgress = status !== null && IN_PROGRESS.has(status)

  // The poll. Keyed on `inProgress` rather than on the data, so it starts
  // when the row starts moving and stops the render after it settles — and
  // `refetch` bumps a counter, so a slow answer cannot race a fast one.
  const { refetch } = domain
  useEffect(() => {
    if (!inProgress) return
    const timer = window.setInterval(refetch, DOMAIN_POLL_MS)
    return () => {
      window.clearInterval(timer)
    }
  }, [inProgress, refetch])

  if (domain.state.status === 'loading') {
    return <Loading label="Loading your domain" lines={3} />
  }
  if (domain.state.status === 'error') {
    return (
      <ErrorState
        failure={domain.state.failure}
        title="Could not load your domain"
        onRetry={domain.refetch}
      />
    )
  }

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>Domain</h2>
        <StatusBadge status={status} />
        {editable ? null : <span className="ar-badge ar-badge--neutral">Read only</span>}
      </div>
      <div className="ar-card__body ar-stack">
        {domain.state.data.tenant.custom_domain === null ||
        domain.state.data.tenant.custom_domain === undefined ? (
          <NoDomain editable={editable} tenantId={tenant.id} onChanged={domain.refetch} />
        ) : (
          <LinkedDomain
            editable={editable}
            tenantId={tenant.id}
            response={domain.state.data}
            onChanged={domain.refetch}
          />
        )}
        {editable ? null : (
          <p className="ar-field__help" style={{ margin: 0 }}>
            Your role can see the domain. A role granted the organisation settings page can change
            it.
          </p>
        )}
      </div>
    </section>
  )
}

/** The header's one-word summary of the row, in the design's tones. */
function StatusBadge({ status }: { status: string | null }): JSX.Element | null {
  if (status === null) return null
  const tone =
    status === 'verified'
      ? 'success'
      : status === 'failed'
        ? 'danger'
        : status === 'pending'
          ? 'warning'
          : 'info'
  const label =
    status === 'verified'
      ? 'Verified'
      : status === 'failed'
        ? 'Failed'
        : status === 'pending'
          ? 'Pending'
          : 'Provisioning'
  return <span className={`ar-badge ar-badge--${tone}`}>{label}</span>
}

/**
 * State **none**, and state **unavailable** once a link has been refused.
 *
 * The input keeps its value across a refusal — the domain was typed correctly,
 * the environment is what is missing — and the unavailable sentence replaces
 * the form's button rather than sitting beside it: there is nothing pressing
 * again would do.
 */
function NoDomain({
  editable,
  tenantId,
  onChanged,
}: {
  editable: boolean
  tenantId: string
  onChanged: () => void
}): JSX.Element {
  const [value, setValue] = useState('')
  const [missing, setMissing] = useState(false)
  const link = useMutation((body: { domain: string }) =>
    call(() =>
      api.PUT('/api/v1/tenants/{tenant_id}/domain', {
        params: { path: { tenant_id: tenantId } },
        body,
      }),
    ),
  )
  const unavailable = link.state.failure?.code === FEATURE_UNAVAILABLE

  if (!editable) {
    return (
      <p className="ar-muted" style={{ margin: 0 }}>
        No custom domain is linked. Your team signs in at the workspace address under Branding.
      </p>
    )
  }

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const domain = value.trim().toLowerCase()
    if (domain === '') {
      setMissing(true)
      return
    }
    setMissing(false)
    const result = await link.run({ domain })
    if (!result.ok) return
    onChanged()
  }

  return (
    <form
      className="ar-stack"
      noValidate
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      {unavailable ? (
        <p className="ar-callout ar-text-body-sm" data-tone="info" role="status">
          Custom domains are not enabled in this environment.
        </p>
      ) : (
        <InlineError failure={link.state.failure} />
      )}
      <TextField
        label="Your domain"
        placeholder="seo.example.com"
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        help="A name you control, such as seo.yourcompany.com. Your team signs in there once it is verified; the workspace address keeps working too."
        error={missing ? 'Enter the domain to link.' : undefined}
        value={value}
        disabled={unavailable}
        onChange={(event) => {
          setValue(event.target.value)
          setMissing(false)
          if (!unavailable) link.reset()
        }}
      />
      {unavailable ? null : (
        <div className="ar-row">
          <Button tone="primary" type="submit" writes busy={link.pending} busyLabel="Linking">
            Link domain
          </Button>
        </div>
      )}
    </form>
  )
}

/**
 * States **pending**, **verifying/provisioning**, **verified** and **failed** —
 * one component, because they are one row at four moments and share the
 * records, the reason and *Remove*.
 */
function LinkedDomain({
  editable,
  tenantId,
  response,
  onChanged,
}: {
  editable: boolean
  tenantId: string
  response: DomainResponse
  onChanged: () => void
}): JSX.Element {
  const { reload } = useTenants()
  const refreshBrand = useBrandRefresh()
  const toast = useToast()
  const [removing, setRemoving] = useState(false)

  const { tenant, records = [], error } = response
  const status = tenant.custom_domain_status ?? 'pending'
  const domain = tenant.custom_domain ?? ''

  const verify = useMutation(() =>
    call(() =>
      api.POST('/api/v1/tenants/{tenant_id}/domain/verify', {
        params: { path: { tenant_id: tenantId } },
      }),
    ),
  )
  const remove = useMutation(() =>
    call(() =>
      api.DELETE('/api/v1/tenants/{tenant_id}/domain', {
        params: { path: { tenant_id: tenantId } },
      }),
    ),
  )

  async function check(): Promise<void> {
    const result = await verify.run(undefined)
    // Either way the row has moved — to `provisioning`, or to carry the miss
    // in `error` — so it is re-read rather than patched from the answer.
    onChanged()
    if (!result.ok) return
    toast.show({
      kind: 'success',
      title: 'Records found',
      body: 'The certificate is being issued. This usually takes a few minutes.',
    })
  }

  async function unlink(): Promise<void> {
    const result = await remove.run(undefined)
    setRemoving(false)
    if (!result.ok) return
    onChanged()
    reload()
    await refreshBrand()
    toast.show({
      kind: 'success',
      title: 'Domain removed',
      body: 'Your team signs in at the workspace address again.',
    })
  }

  // The verify miss is a 409 whose sentence names the record; the same
  // sentence is now on the row as `error`, which the re-read will show. So
  // the mutation's failure is drawn only when it is *not* that — a network
  // fault, a 5xx — or the reason would be printed twice.
  const verifyFailure: ApiFailure | null =
    verify.state.failure !== null && verify.state.failure.code !== 'DOMAIN_NOT_VERIFIED'
      ? verify.state.failure
      : null

  return (
    <div className="ar-stack">
      <InlineError failure={verifyFailure ?? remove.state.failure} />

      {status === 'verified' ? (
        <div className="ar-callout" data-tone="success" role="status">
          <p className="ar-text-label">This domain is live</p>
          <p className="ar-text-body-sm" style={{ marginBottom: 0 }}>
            Your team signs in at{' '}
            <a className="ar-link ar-mono" href={`https://${domain}`}>
              https://{domain}
            </a>
            . Emails and links use it from now on; the workspace address keeps working.
          </p>
        </div>
      ) : null}

      {IN_PROGRESS.has(status) ? (
        <div className="ar-callout ar-row" data-tone="info" role="status" aria-busy="true">
          <span className="ar-spinner" aria-hidden="true" />
          <span>
            <strong className="ar-mono">{domain}</strong> — issuing the certificate. Usually a few
            minutes; this page checks on its own.
          </span>
        </div>
      ) : null}

      {status === 'failed' ? (
        <div className="ar-callout" data-tone="danger" role="alert">
          <p className="ar-text-label">Could not set up {domain}</p>
          <p className="ar-text-body-sm" style={{ marginBottom: 0 }}>
            {error ?? 'The edge did not accept the domain.'} Check the records below, then try
            again.
          </p>
        </div>
      ) : null}

      {status === 'pending' ? (
        <>
          <p className="ar-text-body-sm" style={{ margin: 0 }}>
            Add these two records at your DNS provider for{' '}
            <strong className="ar-mono">{domain}</strong>, then press <strong>Verify</strong>. The
            TXT record proves you control the name; the CNAME is what sends visitors here.
          </p>
          {error ? (
            <p className="ar-callout ar-text-body-sm" data-tone="warning" role="status">
              {error}
            </p>
          ) : null}
        </>
      ) : null}

      {status === 'pending' || status === 'failed' ? <RecordsTable records={records} /> : null}

      {editable ? (
        <div className="ar-row">
          {status === 'pending' ? (
            <Button
              tone="primary"
              writes
              busy={verify.pending}
              busyLabel="Checking DNS"
              onClick={() => void check()}
            >
              Verify
            </Button>
          ) : null}
          {status === 'failed' ? (
            <Button
              tone="primary"
              writes
              busy={verify.pending}
              busyLabel="Checking DNS"
              onClick={() => void check()}
            >
              Try again
            </Button>
          ) : null}
          {IN_PROGRESS.has(status) ? null : (
            <Button
              tone="quiet"
              onClick={() => {
                setRemoving(true)
              }}
            >
              Remove
            </Button>
          )}
        </div>
      ) : null}

      {removing ? (
        <Modal
          title={`Remove ${domain}?`}
          variant="prompt"
          onClose={() => {
            setRemoving(false)
          }}
          footer={
            <>
              <Button
                tone="secondary"
                onClick={() => {
                  setRemoving(false)
                }}
              >
                Keep it
              </Button>
              <Button
                tone="danger"
                writes
                busy={remove.pending}
                busyLabel="Removing"
                onClick={() => void unlink()}
              >
                Remove domain
              </Button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            {status === 'verified'
              ? 'It stops answering straight away, and anyone signed in there is signed out. Your team can still use the workspace address.'
              : 'The records you added can stay or go; nothing here reads them once the domain is removed.'}
          </p>
        </Modal>
      ) : null}
    </div>
  )
}

/** The two records, exactly as the API prints them, each with a copy button. */
function RecordsTable({ records }: { records: readonly DomainRecord[] }): JSX.Element {
  if (records.length === 0) {
    return (
      <p className="ar-muted ar-text-body-sm" style={{ margin: 0 }}>
        No records to publish were returned for this domain.
      </p>
    )
  }
  return (
    <div className="ar-table-wrap">
      <table className="ar-table">
        <thead>
          <tr>
            <th scope="col">Type</th>
            <th scope="col">Name</th>
            <th scope="col">Value</th>
            <th scope="col">
              <span className="ar-visually-hidden">Copy</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr key={`${record.type}:${record.name}`}>
              <td>{record.type}</td>
              <td className="ar-mono">{record.name}</td>
              <td className="ar-mono">{record.value}</td>
              <td>
                <CopyButton value={record.value} label={`${record.type} value`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
